'use memo'

import {
  AgxBadge,
  AgxButton,
  AgxSpacer,
  AgxGlobalSearch,
  AgxTypography,
  CampaignSearcherResult,
  Icon_CheckmarkOutline,
  Icon_FileTextOutline,
  SearchResultType,
  useToast,
  AgxDivider,
} from '@urbanx/agx.ui-components2'
import {
  useGetManualDocuSignUpdateCampaignDetails,
  useInvalidateUseGetManualDocuSignUpdateCampaignDetails,
} from '@urbanx/agx.support.client'
import { useManualUpdateEnvelope } from '@urbanx/agx.docusign.client'
import dayjs from 'dayjs'
import LoadingState from 'components/loadingState/LoadingState'
import Panel from 'components/panel/Panel'
import { useMemo, useState } from 'react'
import SupportBackButton from '../SupportBackButton'
import React from 'react'
import { buildRecipientUpdatePayload } from './manualDocuSignUpdatePayload'

const formatDateTime = (date?: Date | string | null) =>
  date ? dayjs(date).format('DD/MM/YYYY h:mm A') : '-'

const formatStatus = (status?: string | null) => status ?? '-'

const hasRecipientSigned = (status?: string | null) =>
  status === 'Signed' || status === 'Complete'

const getStatusBadgeColour = (status?: string | null) => {
  switch (status) {
    case 'Complete':
    case 'Signed':
      return 'green'
    case 'Opened':
      return 'blue'
    case 'Declined':
    case 'AutoResponded':
      return 'red'
    default:
      return 'grey'
  }
}

const ManualDocuSignUpdates = () => {
  const { pushToast } = useToast()

  const [selectedCampaignId, setSelectedCampaignId] = useState<string>()
  const [pendingActionId, setPendingActionId] = useState<string>()

  const {
    data: campaignDetails,
    isPending: isLoadingCampaignDetails,
    isError: hasCampaignDetailsError,
  } = useGetManualDocuSignUpdateCampaignDetails(
    selectedCampaignId ?? '',
    !!selectedCampaignId
  )

  type Envelope = NonNullable<
    NonNullable<typeof campaignDetails>['envelopes']
  >[number]
  type Recipient = NonNullable<Envelope['recipients']>[number]

  const invalidateManualDocuSignUpdateCampaignDetails =
    useInvalidateUseGetManualDocuSignUpdateCampaignDetails()

  const { manualUpdateEnvelope, manualUpdateEnvelopePending } =
    useManualUpdateEnvelope({
      onSuccess: async () => {
        if (selectedCampaignId) {
          await invalidateManualDocuSignUpdateCampaignDetails(
            selectedCampaignId
          )
        }

        setPendingActionId(undefined)
        pushToast({
          message: 'DocuSign envelope updated',
          type: 'success',
        })
      },
      onError: () => {
        setPendingActionId(undefined)
        pushToast({
          message: 'Error updating DocuSign envelope',
          type: 'error',
        })
      },
    })

  const onItemSelected = (
    itemType: SearchResultType,
    item: CampaignSearcherResult
  ) => {
    if (
      itemType !== SearchResultType.ActiveCampaign &&
      itemType !== SearchResultType.ArchivedCampaign
    ) {
      return
    }

    setSelectedCampaignId(item.campaignId)
    setPendingActionId(undefined)
  }

  const selectedCampaignDetails = useMemo(() => {
    if (!campaignDetails) return []

    return [
      { label: 'Property', value: campaignDetails.propertyAddress },
      { label: 'Agency', value: campaignDetails.agencyName },
    ].filter((detail) => !!detail.value)
  }, [campaignDetails])

  const completeEnvelope = async (envelope: Envelope) => {
    const signedAt = new Date()
    setPendingActionId(`envelope-${envelope.envelopeId}`)

    await manualUpdateEnvelope({
      docuSignEnvelopeId: envelope.docuSignEnvelopeId,
      status: 'completed',
      lastDateSigned: signedAt,
      recipients: (envelope.recipients ?? []).map((recipient) =>
        buildRecipientUpdatePayload(recipient, 'completed', signedAt)
      ),
    })
  }

  const markRecipientAsSigned = async (
    envelope: Envelope,
    recipient: Recipient
  ) => {
    const signedAt = new Date()
    setPendingActionId(`recipient-${recipient.recipientUniqueIdentifier}`)

    await manualUpdateEnvelope({
      docuSignEnvelopeId: envelope.docuSignEnvelopeId,
      status: 'signed',
      recipients: [
        buildRecipientUpdatePayload(recipient, 'signed', signedAt),
      ],
    })
  }

  const hasSelectedCampaign = !!selectedCampaignId
  const envelopes = campaignDetails?.envelopes ?? []
  const isMutating = manualUpdateEnvelopePending

  return (
    <AgxSpacer direction="vertical" gap="spacing-xl" fill>
      <SupportBackButton />
      <Panel title="Manual DocuSign Updates">
        <AgxSpacer direction="vertical" gap="spacing-xl" fill>
          <AgxGlobalSearch
            searchForCampaigns
            searchForArchivedCampaigns
            onItemSelected={onItemSelected}
          />

          {isLoadingCampaignDetails && hasSelectedCampaign && <LoadingState />}

          {hasCampaignDetailsError && (
            <AgxTypography
              id="manual-docusign-updates-error"
              type="body"
              size="small"
            >
              Unable to load DocuSign envelopes.
            </AgxTypography>
          )}

          {campaignDetails && (
            <AgxSpacer direction="vertical" gap="spacing-xl" fill>
              <AgxSpacer direction="horizontal" gap="spacing-xl" fill>
                {selectedCampaignDetails.map((detail) => (
                  <AgxSpacer
                    direction="vertical"
                    key={detail.label}
                    gap="spacing-xs"
                  >
                    <AgxTypography
                      id={`manual-docusign-updates-${detail.label.toLowerCase()}-label`}
                      type="body_ui"
                      size="small"
                    >
                      {detail.label}
                    </AgxTypography>
                    <AgxTypography
                      id={`manual-docusign-updates-${detail.label.toLowerCase()}-value`}
                      type="body"
                      size="medium"
                    >
                      {detail.value}
                    </AgxTypography>
                  </AgxSpacer>
                ))}
              </AgxSpacer>

              {envelopes.length === 0 && (
                <AgxTypography
                  id="manual-docusign-updates-no-envelopes"
                  type="body"
                  size="small"
                >
                  No sent or signed DocuSign envelopes found for this campaign.
                </AgxTypography>
              )}

              <AgxSpacer direction="vertical" gap="spacing-lg" fill>
                {envelopes.map((envelope) => {
                  const envelopeActionId = `envelope-${envelope.envelopeId}`
                  const isCompletingEnvelope =
                    pendingActionId === envelopeActionId && isMutating

                  return (
                    <div
                      key={envelope.envelopeId}
                      className="envelopePanel"
                      data-testid={`manual-docusign-updates-envelope-${envelope.envelopeId}`}
                    >
                      <AgxSpacer direction="vertical" gap="spacing-lg" fill>
                        <AgxSpacer
                          direction="horizontal"
                          gap="spacing-md"
                          fill
                          contentJustification="space-between"
                          contentAlignment="center"
                        >
                          <AgxSpacer direction="vertical" gap="spacing-xs">
                            <AgxSpacer
                              direction="horizontal"
                              gap="spacing-sm"
                              contentAlignment="center"
                            >
                              <Icon_FileTextOutline width={18} height={18} />
                              <AgxTypography
                                id={`manual-docusign-updates-envelope-${envelope.envelopeId}-title`}
                                type="title"
                                size="small"
                              >
                                {envelope.docuSignEnvelopeId}
                              </AgxTypography>
                              <AgxBadge
                                type="colourfill"
                                size="sm"
                                colour={getStatusBadgeColour(envelope.status)}
                                text={formatStatus(envelope.status)}
                              />
                            </AgxSpacer>
                            <AgxTypography
                              id={`manual-docusign-updates-envelope-${envelope.envelopeId}-updated`}
                              type="body_ui"
                              size="small"
                            >
                              Last updated {formatDateTime(envelope.lastUpdate)}
                            </AgxTypography>
                          </AgxSpacer>

                          <AgxButton
                            id={`manual-docusign-updates-envelope-${envelope.envelopeId}-complete`}
                            text={
                              isCompletingEnvelope
                                ? 'Completing'
                                : 'Complete envelope'
                            }
                            variant="primary"
                            size="small"
                            leftIcon={
                              <Icon_CheckmarkOutline width={16} height={16} />
                            }
                            disabled={isMutating}
                            onClick={() => void completeEnvelope(envelope)}
                          />
                        </AgxSpacer>

                        <AgxSpacer direction="vertical" gap="spacing-md" fill>
                          {(envelope.recipients ?? []).map((recipient) => {
                            const recipientActionId = `recipient-${recipient.recipientUniqueIdentifier}`
                            const isMarkingRecipient =
                              pendingActionId === recipientActionId &&
                              isMutating
                            const recipientSigned = hasRecipientSigned(
                              recipient.status
                            )

                            return (
                              <React.Fragment
                                key={recipient.recipientUniqueIdentifier}
                              >
                                <AgxDivider variant={'horizontal'} />
                                <AgxSpacer
                                  direction="horizontal"
                                  gap="lg"
                                  fill
                                  contentJustification="space-between"
                                  contentAlignment="center"
                                >
                                  <AgxSpacer
                                    direction="vertical"
                                    gap="spacing-xs"
                                    fill
                                  >
                                    <AgxTypography
                                      id={`manual-docusign-updates-recipient-${recipient.recipientUniqueIdentifier}-name`}
                                      type="body"
                                      size="medium"
                                    >
                                      {recipient.name || 'Recipient'}
                                    </AgxTypography>
                                    <AgxTypography
                                      id={`manual-docusign-updates-recipient-${recipient.recipientUniqueIdentifier}-email`}
                                      type="body_ui"
                                      size="small"
                                    >
                                      {recipient.email}
                                    </AgxTypography>
                                  </AgxSpacer>

                                  <AgxSpacer
                                    direction="horizontal"
                                    gap="spacing-lg"
                                    contentAlignment="center"
                                  >
                                    <AgxSpacer
                                      direction="vertical"
                                      gap="spacing-xs"
                                    >
                                      <AgxBadge
                                        type="colourfill"
                                        size="sm"
                                        colour={getStatusBadgeColour(
                                          recipient.status
                                        )}
                                        text={formatStatus(recipient.status)}
                                      />
                                      <AgxTypography
                                        id={`manual-docusign-updates-recipient-${recipient.recipientUniqueIdentifier}-updated`}
                                        type="body_ui"
                                        size="small"
                                      >
                                        Last updated{' '}
                                        {formatDateTime(recipient.lastUpdate)}
                                      </AgxTypography>
                                    </AgxSpacer>

                                    {!recipientSigned && (
                                      <AgxButton
                                        id={`manual-docusign-updates-recipient-${recipient.recipientUniqueIdentifier}-mark-signed`}
                                        text={
                                          isMarkingRecipient
                                            ? 'Updating'
                                            : 'Mark as signed'
                                        }
                                        variant="hollow"
                                        size="small"
                                        leftIcon={
                                          <Icon_CheckmarkOutline
                                            width={16}
                                            height={16}
                                          />
                                        }
                                        disabled={isMutating}
                                        onClick={() =>
                                          void markRecipientAsSigned(
                                            envelope,
                                            recipient
                                          )
                                        }
                                      />
                                    )}
                                  </AgxSpacer>
                                </AgxSpacer>
                              </React.Fragment>
                            )
                          })}
                        </AgxSpacer>
                      </AgxSpacer>
                    </div>
                  )
                })}
              </AgxSpacer>
            </AgxSpacer>
          )}
        </AgxSpacer>
      </Panel>
    </AgxSpacer>
  )
}

export default ManualDocuSignUpdates
